import {useMemo, useState} from 'react';
import styled from 'styled-components';
import {graphql, useFragment} from 'react-relay';
import {EMPHASIS} from '@forecast-it/design-system';
import {LabelledButton} from './LabelledButton';
import type {StreamTab_activity$key} from './__generated__/StreamTab_activity.graphql';
import {normaliseForStream} from './normaliseForStream';
import {ReplyComposer} from './ReplyComposer';
import {StreamHistory} from './StreamHistory';
import type {StreamEntry} from './StreamHistoryCard';

const StreamTabFragment = graphql`
	fragment StreamTab_activity on ActivityEvent @relay(plural: true) {
		__typename
		... on MessageActivity {
			id
			createdAt
			body
			bodyHtml
			authorType
			author {
				fullName
			}
			direction
		}
		... on InternalCommentActivity {
			id
			createdAt
			body
			author {
				fullName
			}
			mentionedUsers {
				id
				fullName
			}
		}
		... on EventActivity {
			id
			createdAt
		}
		... on AttachmentActivity {
			id
			createdAt
		}
	}
`;

interface Props {
	entries: StreamTab_activity$key;
	ticketId: string;
	requesterEmail: string;
	onRefetch: () => void;
}

const HistorySection = styled.div`
	margin-top: 24px;
`;

const HistoryHeader = styled.header`
	display: flex;
	align-items: center;
	justify-content: flex-end;
	margin-bottom: 12px;
`;

type SortDir = 'desc' | 'asc';

export function StreamTab({entries, ticketId, requesterEmail, onRefetch}: Props) {
	const data = useFragment<StreamTab_activity$key>(StreamTabFragment, entries);
	const [sortDir, setSortDir] = useState<SortDir>('desc');

	const streamEntries = useMemo(() => {
		const result: StreamEntry[] = [];
		for (const entry of data) {
			if (entry.__typename === '%other') continue;
			const normalised = normaliseForStream(entry);
			if (normalised) result.push(normalised);
		}
		result.sort((a, b) => {
			const diff = new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
			return sortDir === 'asc' ? diff : -diff;
		});
		return result;
	}, [data, sortDir]);

	return (
		<div data-testid="stream-tab">
			<ReplyComposer ticketId={ticketId} requesterEmail={requesterEmail} onSent={onRefetch} />
			<HistorySection>
				<HistoryHeader>
					<LabelledButton
						emphasis={EMPHASIS.MEDIUM}
						onPress={() => setSortDir(d => (d === 'desc' ? 'asc' : 'desc'))}
						label="Toggle sort order"
						data-testid="stream-sort-toggle"
					>
						{sortDir === 'desc' ? 'Newest first' : 'Oldest first'}
					</LabelledButton>
				</HistoryHeader>
				<StreamHistory entries={streamEntries} />
			</HistorySection>
		</div>
	);
}
